import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	FormControlLabel,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { merchantTableColumns, mearchatSkuDummyData } from "./data";
import { useEffect, useState } from "react";
import { converAttrToDatagridCol, createdByColumns, selectOptionsKeyName, statusOptions } from "data/globalData";
import { AuthState } from "context/AuthContext";
import { GlobalState } from "context/GlobalContext";
import { parseErrorMessage } from "utils/ErrorFunctions/parseErrorMessage";
import axios from "axios";
import { DockRounded, FileDownload, TextSnippet, UploadRounded, VisibilityOutlined } from "@mui/icons-material";
import DatagridWithApiPagination from "components/ReusableComponents/DatagridWithApiPagination/DatagridWithApiPagination";
import SkuSectionTanstack from "../CreateShoot/SkuSection/SkuSectionTanstack";
import {
	sampleStatusColumn,
	shootMerchantSampleDetailsColumns,
	shootMerchantSampleImgUrlCol,
	shootSkuIcodeColumn,
	shootStudioSampleGrnImgUrlCol,
	shootStudioSampleImgUrlCol,
} from "../ShootsDashboard/data";
import { set } from "lodash";
import RenderInputField from "components/PimDashboard/CommonComponents/RenderInputField/RenderInputField";
import { grey, orange } from "@mui/material/colors";
import SampleHistoryModal from "./SampleHistoryModal/SampleHistoryModal";
import { custom } from "data/authData";
import { createUpdatedDataPayload } from "utils/createUpdatedDataPayload";
import UploadModal from "./UploadModal/UploadModal";
import ExcelJS from "exceljs";

const merchantReadyRequiredKeys = Object.values(shootMerchantSampleDetailsColumns)
	.filter((column) => column.key !== "sample_ready_date")
	.map((column) => column.key);
const merchantUploadColumns = [
	shootSkuIcodeColumn,
	...Object.values(shootMerchantSampleDetailsColumns).filter((column) => column.key !== "sample_ready_date"),
];

const allowedMerchantReadyStatuses = new Set([null, undefined, "", statusOptions.REJECTED.key]);
const bulkMerchantReadyStatuses = new Set([null, undefined, ""]);
const hasEmptyValue = (value) => value === undefined || value === null || value === "";
const formatMySqlDateTime = (date = new Date()) => {
	const pad = (value) => String(value).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
		date.getMinutes(),
	)}:${pad(date.getSeconds())}`;
};

const SAMPLE_DETAIL_PREVIEW_WIDTH = 140;
const SAMPLE_DETAIL_PREVIEW_HEIGHT = 200;
const MIN_PRODUCTION_GAP_DAYS = 5;
const plannedSampleDateKey = shootMerchantSampleDetailsColumns.planned_sample_date.key;
const expectedProductionInwardKey = shootMerchantSampleDetailsColumns.expected_production_inward.key;

const parseDateOnly = (value) => {
	if (hasEmptyValue(value)) return null;
	const match = `${value}`.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (match) {
		return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
	}
	const date = value instanceof Date ? value : new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const addDays = (date, days) => {
	const next = new Date(date);
	next.setDate(next.getDate() + days);
	return next;
};

const formatDateInputValue = (date) => {
	const pad = (value) => String(value).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const getProductionDateError = (plannedValue, expectedValue) => {
	const plannedDate = parseDateOnly(plannedValue);
	const expectedDate = parseDateOnly(expectedValue);
	if (!plannedDate || !expectedDate) return null;

	const diffDays = Math.round((expectedDate - plannedDate) / (24 * 60 * 60 * 1000));
	if (diffDays <= 0) {
		return "Expected Production Inward must be greater than Planned Sample Handover";
	}
	if (diffDays < MIN_PRODUCTION_GAP_DAYS) {
		return "Expected Production Inward must be at least 5 days after Planned Sample Handover";
	}
	return null;
};

const getFileExtensionFromUrl = (url = "") => {
	const sanitizedUrl = `${url}`.split("?")[0];
	const match = sanitizedUrl.match(/\.([a-zA-Z0-9]+)$/);
	return match?.[1]?.toLowerCase() || "jpg";
};

const sanitizeDownloadName = (name = "image") => `${name}`.trim().replace(/[^\w.-]+/g, "_") || "image";

const triggerDirectDownload = (url, filename) => {
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = filename;
	anchor.rel = "noopener";
	anchor.target = "_blank";
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
};

const triggerBlobDownload = (blob, filename) => {
	const blobUrl = window.URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = blobUrl;
	anchor.download = filename;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	window.URL.revokeObjectURL(blobUrl);
};

const SampleDetailImageBox = ({ src, alt = "preview", emptyText = "Not uploaded yet", fileName }) => {
	const [downloading, setDownloading] = useState(false);

	const handleDownload = async (event) => {
		event.stopPropagation();
		if (!src || downloading) return;
		const downloadName = `${sanitizeDownloadName(fileName || alt)}.${getFileExtensionFromUrl(src)}`;
		setDownloading(true);
		try {
			const response = await fetch(src, { mode: "cors" });
			if (!response.ok) throw new Error("Download failed");
			const blob = await response.blob();
			triggerBlobDownload(blob, downloadName);
		} catch {
			triggerDirectDownload(src, downloadName);
		} finally {
			setDownloading(false);
		}
	};

	return (
		<Stack spacing={1} alignItems="flex-start">
			<Paper
				elevation={0}
				sx={{
					width: SAMPLE_DETAIL_PREVIEW_WIDTH,
					height: SAMPLE_DETAIL_PREVIEW_HEIGHT,
					maxWidth: "100%",
					borderRadius: "8px",
					overflow: "hidden",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: grey[100],
					flexShrink: 0,
					border: `1px solid ${grey[300]}`,
					pointerEvents: src ? "auto" : "none",
				}}>
				{src ? (
					<img
						src={src}
						alt={alt}
						style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
					/>
				) : (
					<Box textAlign="center" px={1.5}>
						<VisibilityOutlined sx={{ fontSize: 22, color: grey[500], mb: 0.5 }} />
						<Typography variant="caption" display="block" color="text.secondary">
							{emptyText}
						</Typography>
					</Box>
				)}
			</Paper>
			{src ? (
				<Button
					size="small"
					variant="outlined"
					onClick={handleDownload}
					disabled={downloading}
					startIcon={downloading ? <CircularProgress size={14} /> : <FileDownload />}
					sx={{
						width: SAMPLE_DETAIL_PREVIEW_WIDTH,
						maxWidth: "100%",
						textTransform: "none",
					}}>
					{downloading ? "Downloading" : "Download"}
				</Button>
			) : null}
		</Stack>
	);
};

const MerchantData = () => {
	const { user } = AuthState();
	const { setLoadingOpen, setSnackBarOpen, setSnackBar, snackBar } = GlobalState();
	const disableEdit = user?.role?.[custom.SAMPLE_INWARD_VIEWER.key] === true;

	const [selectedSkus, setSelectedSkus] = useState([]);
	const [allSkus, setAllSkus] = useState([]);
	const [editModal, setEditModal] = useState(false);
	const [historyModal, setHistoryModal] = useState(false);
	const [originalData, setOriginalData] = useState(null);
	const [editedData, setEditedData] = useState(null);
	const [selectedHistorySku, setSelectedHistorySku] = useState(null);
	const [bulkEdit, setBulkEdit] = useState(false);
	const [markAsReady, setMarkAsReady] = useState(false);
	const [uploadModal, setUploadModal] = useState(false);
	const [uploadRows, setUploadRows] = useState([]);
	const [markUploadAsReady, setMarkUploadAsReady] = useState(false);

	useEffect(() => {
		if (selectedSkus.length > 1) setBulkEdit(true);
		else setBulkEdit(false);
	}, [selectedSkus]);

	const selectedSkuRows = allSkus.filter((item) => selectedSkus.includes(item[shootSkuIcodeColumn.key]));
	const isBulkReadyEligible =
		selectedSkus.length > 0 &&
		selectedSkuRows.length === selectedSkus.length &&
		selectedSkuRows.every((item) => bulkMerchantReadyStatuses.has(item?.[sampleStatusColumn.key]));
	const isSingleReadyEligible = allowedMerchantReadyStatuses.has(editedData?.[sampleStatusColumn.key]);
	const canShowMarkAsReady = bulkEdit ? isBulkReadyEligible : isSingleReadyEligible;

	const handleEditModalClose = () => {
		setEditModal(false);
		setEditedData(null);
		setOriginalData(null);
		setSelectedSkus([]);
		setMarkAsReady(false);
	};

	const handleIcodeSelect = (data) => {
		setEditedData(data);
		setOriginalData(data);
		setMarkAsReady(false);
		setEditModal(true);
	};

	const handleBulkEditButtonClick = () => {
		if (selectedSkus.length === 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Please select at least one SKU to edit",
				severity: "warning",
			});
			return;
		}
		setEditedData({});
		setOriginalData({});
		setMarkAsReady(isBulkReadyEligible);
		setEditModal(true);
	};

	const validateReadyData = (data) => {
		if (!canShowMarkAsReady) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				open: true,
				message: "Only empty or rejected samples can be marked as ready",
				severity: "error",
			});
			return false;
		}

		const missingFields = merchantReadyRequiredKeys.filter((key) => hasEmptyValue(data?.[key]));

		if (missingFields.length > 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				open: true,
				message: "Fill all merchant sample details before marking as ready",
				severity: "error",
			});
			return false;
		}

		return true;
	};

	const validateProductionDates = (data) => {
		const plannedFromForm = data?.[plannedSampleDateKey];
		const expectedFromForm = data?.[expectedProductionInwardKey];

		if (bulkEdit) {
			const plannedProvided = !hasEmptyValue(plannedFromForm);
			const expectedProvided = !hasEmptyValue(expectedFromForm);
			if (!plannedProvided && !expectedProvided) return true;

			const invalidRow = selectedSkuRows.find((row) => {
				const planned = plannedProvided ? plannedFromForm : row?.[plannedSampleDateKey];
				const expected = expectedProvided ? expectedFromForm : row?.[expectedProductionInwardKey];
				return Boolean(getProductionDateError(planned, expected));
			});

			if (!invalidRow) return true;

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				open: true,
				message:
					getProductionDateError(
						plannedProvided ? plannedFromForm : invalidRow?.[plannedSampleDateKey],
						expectedProvided ? expectedFromForm : invalidRow?.[expectedProductionInwardKey],
					) || "Expected Production Inward must be at least 5 days after Planned Sample Handover",
				severity: "error",
			});
			return false;
		}

		const error = getProductionDateError(plannedFromForm, expectedFromForm);
		if (!error) return true;

		setSnackBarOpen(true);
		setSnackBar({
			...snackBar,
			open: true,
			message: error,
			severity: "error",
		});
		return false;
	};

	const handleDataSave = async (data) => {
		const workingData = { ...(data || {}) };

		if (markAsReady) {
			if (!validateReadyData(workingData)) return;

			workingData[sampleStatusColumn.key] = statusOptions.READY.key;
			workingData.sample_ready_date = formatMySqlDateTime();
		}

		if (!validateProductionDates(workingData)) return;

		const payload = bulkEdit
			? Object.keys(workingData || {}).reduce((acc, key) => {
					if (!hasEmptyValue(workingData[key])) {
						acc[key] = workingData[key];
					}
					return acc;
				}, {})
			: createUpdatedDataPayload(workingData, originalData || {}, {
					...shootMerchantSampleDetailsColumns,
					[sampleStatusColumn.key]: sampleStatusColumn,
				});

		if (markAsReady && payload.sample_ready_date == null) {
			payload.sample_ready_date = workingData.sample_ready_date;
			payload[sampleStatusColumn.key] = workingData[sampleStatusColumn.key];
		}

		if (Object.keys(payload).length === 0) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				open: true,
				message: "No changes made",
				severity: "warning",
			});
			return;
		}

		setLoadingOpen(true);
		try {
			if (bulkEdit) {
				await axios.put(`api/studio/bulk_update_shoot_merchant_sample_details`, {
					body: payload,
					icode_list: selectedSkus,
				});

				setAllSkus((prev) =>
					prev.map((item) => (selectedSkus.includes(item[shootSkuIcodeColumn.key]) ? { ...item, ...payload } : item)),
				);
			} else {
				await axios.put(`api/studio/update_shoot_merchant_sample_details/${data[shootSkuIcodeColumn.key]}`, payload);

				setAllSkus((prev) =>
					prev.map((item) =>
						item[shootSkuIcodeColumn.key] === data[shootSkuIcodeColumn.key] ? { ...item, ...payload } : item,
					),
				);
			}

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				open: true,
				message: "Data updated successfully",
				severity: "success",
			});
			handleEditModalClose();
		} catch (err) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				severity: "error",
				message: parseErrorMessage(err),
			});
		} finally {
			setLoadingOpen(false);
			setSelectedSkus([]);
		}
	};

	const handleHistoryClick = (selectedRow) => {
		setSelectedHistorySku(selectedRow);
		setHistoryModal(true);
	};

	const validUploadRows = uploadRows.filter((row) => row.__isValid !== false);
	const canMarkUploadAsReady =
		validUploadRows.length > 0 && validUploadRows.every((row) => allowedMerchantReadyStatuses.has(row.__currentStatus));

	const handleDownloadUploadFormat = async () => {
		const workbook = new ExcelJS.Workbook();
		const worksheet = workbook.addWorksheet("Merchant Data Upload");

		worksheet.addRow(merchantUploadColumns.map((column) => column.key));
		worksheet.addRow(["i_code_value", "2026-03-11 00:00:00", "2026-03-18 00:00:00", "Vendor Name", "1000", "Remarks"]);

		const buffer = await workbook.xlsx.writeBuffer();
		const blob = new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = "merchant_data_upload_format.xlsx";
		link.click();
		URL.revokeObjectURL(link.href);
	};

	const handleUploadSave = async (rows, { transitionChecked = false } = {}) => {
		if (transitionChecked) {
			const rowsWithMissingFields = rows.filter((row) => {
				if (row.__isValid === false) return false;
				const mergedData = { ...(row.__currentRow || {}), ...row };
				return merchantReadyRequiredKeys.some((key) => hasEmptyValue(mergedData[key]));
			});

			if (rowsWithMissingFields.length > 0) {
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					open: true,
					message: "All valid rows must have required merchant fields filled before marking as ready",
					severity: "error",
				});
				return false;
			}
		}

		const validRows = rows
			.filter((row) => row.__isValid !== false)
			.map((row) => {
				const icode = row[shootSkuIcodeColumn.key];
				const payload = merchantUploadColumns.reduce((acc, column) => {
					if (column.key === shootSkuIcodeColumn.key) return acc;
					if (!hasEmptyValue(row[column.key])) {
						acc[column.key] = row[column.key];
					}
					return acc;
				}, {});

				if (transitionChecked) {
					payload[sampleStatusColumn.key] = statusOptions.READY.key;
					payload.sample_ready_date = formatMySqlDateTime();
				}

				return { icode, payload };
			})
			.filter((row) => !hasEmptyValue(row.icode) && Object.keys(row.payload).length > 0);

		if (!validRows.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				open: true,
				message: "Upload file must include i_code and at least one editable merchant field",
				severity: "warning",
			});
			return false;
		}

		setLoadingOpen(true);
		try {
			const results = await Promise.allSettled(
				validRows.map(({ icode, payload }) => axios.put(`api/studio/update_shoot_merchant_sample_details/${icode}`, payload)),
			);

			const successfulRows = validRows.filter((_, index) => results[index].status === "fulfilled");
			const failedCount = results.length - successfulRows.length;
			const payloadByIcode = successfulRows.reduce((acc, row) => {
				acc[row.icode] = row.payload;
				return acc;
			}, {});

			if (successfulRows.length > 0) {
				setAllSkus((prev) =>
					prev.map((item) => {
						const rowPayload = payloadByIcode[item[shootSkuIcodeColumn.key]];
						return rowPayload ? { ...item, ...rowPayload } : item;
					}),
				);
			}

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				open: true,
				message:
					failedCount > 0
						? `${successfulRows.length} rows uploaded successfully, ${failedCount} failed`
						: `${successfulRows.length} rows uploaded successfully`,
				severity: failedCount > 0 ? "warning" : "success",
			});

			if (failedCount === 0) {
				setUploadRows([]);
				setMarkUploadAsReady(false);
			}

			return failedCount === 0;
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				open: true,
				message: parseErrorMessage(error),
				severity: "error",
			});
			return false;
		} finally {
			setLoadingOpen(false);
		}
	};

	const handleUploadPreviewRows = async (rows) => {
		const icodes = [...new Set(rows.map((row) => row[shootSkuIcodeColumn.key]).filter((value) => !hasEmptyValue(value)))];

		if (!icodes.length) {
			return rows.map((row) => ({
				...row,
				__isValid: false,
				__currentStatus: "",
				__note: "Missing i_code",
			}));
		}

		let existingRowsMap = new Map();

		try {
			const response = await axios.post(
				"/api/studio/skus_combined_data",
				{ filter: { i_code_list: icodes } },
				{ params: { limit: icodes.length, offset: 0 } },
			);
			const apiRows = response?.data?.result?.rows || [];
			existingRowsMap = new Map(apiRows.map((item) => [item[shootSkuIcodeColumn.key], item]));
		} catch (error) {
			existingRowsMap = new Map(allSkus.map((item) => [item[shootSkuIcodeColumn.key], item]));
		}

		const previewRows = rows.map((row, index) => {
			const currentRow = existingRowsMap.get(row[shootSkuIcodeColumn.key]);

			if (!currentRow) {
				return {
					...row,
					__rowId: row.__rowId || `${row[shootSkuIcodeColumn.key] || "row"}-${index}`,
					__isValid: false,
					__currentStatus: "",
					__note: "i_code not found",
					__currentRow: null,
				};
			}

			return {
				...row,
				__rowId: row.__rowId || `${row[shootSkuIcodeColumn.key] || "row"}-${index}`,
				__isValid: true,
				__currentStatus: currentRow[sampleStatusColumn.key] || "",
				__note: "",
				__currentRow: currentRow,
			};
		});

		setUploadRows(previewRows);
		setMarkUploadAsReady(false);

		return previewRows;
	};

	const productionDateError = getProductionDateError(
		editedData?.[plannedSampleDateKey],
		editedData?.[expectedProductionInwardKey],
	);
	const plannedDate = parseDateOnly(editedData?.[plannedSampleDateKey]);
	const minExpectedProductionDate = plannedDate
		? formatDateInputValue(addDays(plannedDate, MIN_PRODUCTION_GAP_DAYS))
		: undefined;

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 64px - 72px)",
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				boxSizing: "border-box",
				px: "10px",
			}}>
			<Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
				<SkuSectionTanstack
					selectedSkus={selectedSkus}
					setSelectedSkus={setSelectedSkus}
					shootData={{}}
					showGrnColumns={true}
					onRowClick={handleIcodeSelect}
					minHeight="100%"
					maxHeight="100%"
					skusState={allSkus}
					setSkusState={setAllSkus}
					hideSearchHeader={true}
					showActionColumn={true}
					actionList={[{ name: "History", onClick: handleHistoryClick }]}
					disableSelection={disableEdit}
					enableExcelExport={true}
					exportFileName="sample_sku_details.xlsx"
					childrenAboveTable={
						<Box flex={1} alignItems="center" display="flex">
							<Stack direction="row" spacing={1}>
								<Button
									variant="outlined"
									size="small"
									onClick={handleDownloadUploadFormat}
									startIcon={<TextSnippet />}>
									Upload Format
								</Button>
								<Button
									variant="contained"
									size="small"
									onClick={() => setUploadModal(true)}
									disabled={disableEdit}
									startIcon={<UploadRounded />}>
									Upload Data
								</Button>
								<Button variant="contained" disabled={!bulkEdit} onClick={handleBulkEditButtonClick} size="small">
									Bulk Edit
								</Button>
							</Stack>
						</Box>
					}
				/>
			</Box>
			<Dialog fullWidth maxWidth="lg" open={editModal} onClose={handleEditModalClose}>
				<Typography variant="h6" p={2} pb={1}>
					{bulkEdit ? "Bulk" : ""}Edit Merchant Sample Details
				</Typography>
				<Box sx={{ p: 2, pt: 1, borderBottom: "1px solid #ccc" }}>
					{/* disply icode chips */}
					{bulkEdit ? (
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
							<Typography variant="h6" fontSize={16} sx={{ width: "100%" }} color={grey[700]}>
								Selected Icodes
							</Typography>
							{selectedSkus.map((sku) => (
								<Box
									key={sku}
									sx={{
										backgroundColor: grey[400],
										borderRadius: "5px",
										padding: "5px",
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}>
									<Typography variant="subtitle1">{sku}</Typography>
								</Box>
							))}
						</Box>
					) : (
						<Typography variant="subtitle1">SKU ICODE: {editedData?.[shootSkuIcodeColumn.key]}</Typography>
					)}
				</Box>
				<Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
					<Typography
						sx={{
							width: "100%",
							fontSize: 15,
							fontWeight: 600,
							color: productionDateError ? orange[900] : orange[800],
						}}>
						{productionDateError
							? `* ${productionDateError}`
							: "* Expected Production Inward must be greater than Planned Sample Handover, with a minimum difference of 5 days."}
					</Typography>
					{Object.values(shootMerchantSampleDetailsColumns).map((column) => {
						const isExpectedProduction = column.key === expectedProductionInwardKey;
						return (
							<RenderInputField
								key={column.key}
								column={column}
								data={editedData}
								setData={setEditedData}
								selectOptObjName={selectOptionsKeyName}
								width="32%"
								longWidth="66%"
								error={isExpectedProduction && Boolean(productionDateError)}
								inputProps={
									isExpectedProduction && minExpectedProductionDate ? { min: minExpectedProductionDate } : undefined
								}
							/>
						);
					})}
				</Box>
				{!bulkEdit && (
					<Box sx={{ px: 2, pb: 2, display: "flex", flexWrap: "wrap", gap: 3 }}>
						<Box>
							<Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
								<Typography variant="h6" fontSize={16} color={grey[700]}>
									Sample Image
								</Typography>
								<Typography variant="caption" color="text.secondary">
									View only
								</Typography>
							</Stack>
							<SampleDetailImageBox
								src={
									editedData?.[shootStudioSampleImgUrlCol.key] ||
									editedData?.[shootMerchantSampleImgUrlCol.key]
								}
								alt="Sample"
								emptyText="Not uploaded yet"
								fileName={`${editedData?.[shootSkuIcodeColumn.key] || "sku"}_sample`}
							/>
						</Box>
						<Box>
							<Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
								<Typography variant="h6" fontSize={16} color={grey[700]}>
									GRN Image
								</Typography>
								<Typography variant="caption" color="text.secondary">
									View only
								</Typography>
							</Stack>
							<SampleDetailImageBox
								src={editedData?.[shootStudioSampleGrnImgUrlCol.key]}
								alt="GRN"
								emptyText="Not uploaded yet"
								fileName={`${editedData?.[shootSkuIcodeColumn.key] || "sku"}_grn`}
							/>
						</Box>
					</Box>
				)}
				<Stack sx={{ px: 2, pb: 1 }}>
					<FormControlLabel
						control={
							<Checkbox
								checked={markAsReady}
								onChange={(e) => setMarkAsReady(e.target.checked)}
								disabled={disableEdit || !canShowMarkAsReady}
							/>
						}
						label="Mark as ready"
					/>
					{!canShowMarkAsReady && (
						<Typography variant="caption" color="warning.main">
							{bulkEdit
								? "* Mark as ready is enabled only when all selected SKUs have empty or rejected status."
								: "*Mark as ready is enabled only when status is empty or rejected."}
						</Typography>
					)}
				</Stack>
				<DialogActions>
					<Button variant="outlined" onClick={handleEditModalClose}>
						Cancel
					</Button>
					<Button
						autoFocus
						variant="contained"
						sx={{ mr: 1 }}
						onClick={() => handleDataSave(editedData)}
						disabled={disableEdit}>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			<UploadModal
				modal={uploadModal}
				setModal={setUploadModal}
				columns={merchantUploadColumns}
				transformRows={handleUploadPreviewRows}
				onRowsChange={setUploadRows}
				transitionConfig={{
					label: "Mark as ready",
					checked: markUploadAsReady,
					onChange: setMarkUploadAsReady,
					canEnable: canMarkUploadAsReady,
					note: "Mark as ready is enabled only when all valid uploaded rows have empty or rejected status.",
				}}
				callbackFun={handleUploadSave}
			/>

			<SampleHistoryModal skuDetails={selectedHistorySku} modal={historyModal} setModal={setHistoryModal} />
		</Box>
	);
};

export default MerchantData;
